/**
 * Centralized error handling utility
 */

import { TOAST_TYPES } from '../constants';

class ErrorHandler {
  /**
   * Logs error to console in development
   * @param {Error} error
   * @param {string} context
   */
  static logError(error, context = '') {
    if (import.meta.env.DEV) {
      console.error(`[${context}]`, error);
    }
  }

  /**
   * Extracts user-friendly error message from error object
   * @param {Error} error
   * @returns {string}
   */
  static getErrorMessage(error) {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }

    if (error.response?.data?.error) {
      return typeof error.response.data.error === 'string'
        ? error.response.data.error
        : error.response.data.error.message || 'An error occurred';
    }

    if (error.message) {
      return error.message;
    }

    return 'An unexpected error occurred';
  }

  /**
   * Handles API error with toast notification
   * @param {Error} error
   * @param {Function} showToast - Toast notification function
   * @param {string} context - Context for logging
   * @param {string} defaultMessage - Default message if none extracted
   */
  static handleApiError(error, showToast = null, context = '', defaultMessage = null) {
    this.logError(error, context);

    const message = defaultMessage || this.getErrorMessage(error);

    if (showToast) {
      showToast(message, TOAST_TYPES.ERROR);
    }

    return message;
  }

  /**
   * Handles network error
   * @param {Error} error
   * @param {Function} showToast
   */
  static handleNetworkError(error, showToast = null) {
    const message = 'Network error. Please check your connection.';

    this.logError(error, 'Network');

    if (showToast) {
      showToast(message, TOAST_TYPES.ERROR);
    }

    return message;
  }

  /**
   * Handles validation errors
   * @param {Object} errors - Validation errors object
   * @returns {string} - First error message
   */
  static getValidationMessage(errors) {
    if (!errors || typeof errors !== 'object') return '';

    const firstKey = Object.keys(errors)[0];
    const firstError = errors[firstKey];

    return Array.isArray(firstError) ? firstError[0] : firstError;
  }

  /**
   * Checks if error is authentication error
   * @param {Error} error
   * @returns {boolean}
   */
  static isAuthError(error) {
    return error.response?.status === 401;
  }

  /**
   * Checks if error is forbidden error
   * @param {Error} error
   * @returns {boolean}
   */
  static isForbiddenError(error) {
    return error.response?.status === 403;
  }

  /**
   * Checks if error is not found error
   * @param {Error} error
   * @returns {boolean}
   */
  static isNotFoundError(error) {
    return error.response?.status === 404;
  }

  /**
   * Checks if error is validation error
   * @param {Error} error
   * @returns {boolean}
   */
  static isValidationError(error) {
    return error.response?.status === 422;
  }
}

export default ErrorHandler;
